import { useQueryClient } from "@tanstack/react-query";
import * as React from "react";
import { useCurrentAccount } from "./backpack-console/CurrentAccountManager";
import SitesContext, { SitesContextType } from "./context/SitesContext";
import { useCurrentUser } from "./context/UserContext";
import { useFeatureFlagsReady } from "./featureFlags";
import { useFeatureFlag } from "@src/backpack-console/featureFlags";
import {
  handleRefreshSite,
  SITE_SUMMARY_FIELDS,
} from "./global_functions/sites/sitesHelpers";
import {
  useSiteSummaryQueryKey,
  useSitesSummaryQuery,
} from "./global_functions/sites/sitesQueries";
import { SummaryOverview } from "./types";

const DEFAULT_SITES: SummaryOverview[] = [];

export function SitesContextProvider({ children }: React.PropsWithChildren) {
  const { superAdmin, id } = useCurrentUser();
  const currentUserExists = typeof id === "number";

  const isInactiveSitesEnabled =
    useFeatureFlag("inactive-sites", "off") === "on" && superAdmin;
  const featureFlagsReady = useFeatureFlagsReady();

  const { accountId } = useCurrentAccount();

  const isCurrentAccountSitesQueryEnabled =
    featureFlagsReady && currentUserExists && typeof accountId === "number";
  const { data: currentAccountSites, isPending } = useSitesSummaryQuery({
    enabled: isCurrentAccountSitesQueryEnabled,
    includeInactiveSites: isInactiveSitesEnabled,
    accountId,
  });

  const queryClient = useQueryClient();

  const queryKey = useSiteSummaryQueryKey({
    includeInactiveSites: isInactiveSitesEnabled,
    accountId,
  });

  const setSites = React.useCallback(
    (cb: (prev?: SummaryOverview[]) => SummaryOverview[]) =>
      queryClient.setQueryData<SummaryOverview[]>(queryKey, cb),
    [queryClient, queryKey]
  );

  const refreshSite = React.useCallback(
    (siteId: string | number) =>
      handleRefreshSite(
        siteId,
        setSites,
        isInactiveSitesEnabled,
        SITE_SUMMARY_FIELDS
      ),
    [setSites, isInactiveSitesEnabled]
  );

  const siteContextValue = React.useMemo(
    (): SitesContextType => ({
      currentAccountSites: currentAccountSites ?? DEFAULT_SITES,
      refreshSite,
      isLoading: isPending,
    }),
    [currentAccountSites, refreshSite, isPending]
  );

  return (
    <SitesContext.Provider value={siteContextValue}>
      {children}
    </SitesContext.Provider>
  );
}
